<template>
  <div>
    <div
      v-if="selectedLocation.isLoading && !isProcessingLookup"
      class="section"
    >
      <div class="loader" />
    </div>

    <div
      v-else
      class="section center"
    >
      <div class="container max-width-mobile">
        <h1 class="title title--2 primary">
          Make a Payment
        </h1>

        <p>To get started, please enter the check number displayed on your receipt:</p>

        <validated-form
          name="lookup"
          :disabled="isProcessingLookup"
          @valid-submit="submitLookupForm"
        >
          <div class="input-with-button">
            <div class="input-with-button__input">
              <text-input
                v-model="ticketNumber"
                type="text"
                label="Check Number"
                name="ticketNumber"
                icon-class="fa-receipt"
                :validations="{
                  required: true
                }"
              />
            </div>

            <div class="input-with-button__button">
              <button
                class="button button--secondary button--icon"
                type="button"
                @click="$openModal('ticketNumberHelp')"
              >
                <span class="icon fa-question" />

                <span class="ada-text">Check Number Help</span>
              </button>
            </div>
          </div>

          <button
            class="button button--primary button--large"
            :class="{
              'button--loading': isProcessingLookup
            }"
            type="submit"
          >
            Submit
          </button>
        </validated-form>
      </div>
    </div>



    <modal
      name="ticketNumberHelp"
      modal-style="compact"
    >
      <div class="section">
        <div class="center">
          <span class="icon far icon--medium icon--block fa-receipt" />

          <h2 class="title title--2">
            {{ ticketNumberInstructions.title || 'Check Number?' }}
          </h2>
        </div>

        <p>
          <pre>
            {{ ticketNumberInstructions.description || 'Your check number, also referred to as order or check number, is typically a 3-10 digit number printed near the top of your receipt.' }}
          </pre>
        </p>

        <button
          class="button button--primary button--large"
          type="button"
          @click="$closeModal('ticketNumberHelp')"
        >
          Okay
        </button>
      </div>
    </modal>
  </div>
</template>



<script>
  import errorHandlers from '@/services/error-handlers';
  import http from '@/services/http';
  import logger from '@/services/logger';
  import { mapGetters } from 'vuex';



  export default {
    name: 'MakeAPaymentPage',

    metaInfo() {
      return {
        title: 'Make a Payment'
      };
    },

    data() {
      return {
        isProcessingLookup: false,
        tableNumber: null,
        ticketNumber: null,
        ticketNumberInstructions: {
          title: null,
          description: null
        }
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['session'])
    },

    created() {
      this.createdHandler();
    },

    methods: {
      createdHandler() {
        this.tableNumber = this.session.route.tableNumber;
        this.ticketNumber = this.session.route.ticketNumber;

        this.loadLocation();
      },

      async loadDynamicContent() {
        try {
          const response = await http.get('/cardfree-api/v1/merchant/ticket-number-instructions', {
            params: {
              merchantLocationId: this.session.route.merchantLocationId
            }
          });

          this.ticketNumberInstructions.title = response.data.title;
          this.ticketNumberInstructions.description = response.data.description;
        }

        catch (error) {
          logger.error(error);
        }
      },

      loadLocation() {
        this.$store.dispatch('locations/getLocationConditionally');

        this.loadDynamicContent();
      },

      redirectToOrder() {
        const { tableNumber, ticketNumber } = this;
        const query = {
          ...this.$route.query
        };

        if (tableNumber) {
          query.tb = tableNumber;
        }

        if (ticketNumber) {
          query.tn = ticketNumber;
        }

        this.$router.push({
          name: 'payAtTable',
          params: {
            merchantId: this.$route.params.merchantId
          },
          query
        });
      },

      async submitLookupForm() {
        this.isProcessingLookup = true;

        try {
          const { posLocationId } = this.session.route;
          const { merchantLocationId } = this.session.route;
          const { tableNumber } = this;
          const { ticketNumber } = this;

          this.$store.dispatch('socket/initialize');

          this.$store.dispatch('orders/normalizeOrderDetails', {
            defaultFulfillmentType: 'standard',
            defaultOrderType: 'pay',
            orderTypes: ['pay', 'table']
          });

          await this.$store.dispatch('orders/getAllOrdersAtTable', {
            query: {
              merchantLocationId,
              posLocationId,
              tableNumber,
              ticketNumber
            }
          });

          this.redirectToOrder();
        }

        catch (error) {
          errorHandlers.generic(error, {
            fallbackMessage: 'Invalid check number or check no longer available for payment. Please verify and try again.'
          });
        }

        finally {
          this.isProcessingLookup = false;
        }
      }
    }
  };
</script>
